import * as React from 'react';

/**
 * Synchronous same-click double-submit guard for a mutation confirm button
 * (breakdown-dry-fixes fix #4). A `mutation.isPending`-based `disabled` prop
 * alone is asynchronous — React only flips `isPending` after a re-render
 * commits, so two clicks landing in the same tick (a fast double-click, or a
 * duplicate synthetic event) can both pass the `disabled` check and fire the
 * action twice. This hook adds a plain `useRef` boolean flag set
 * synchronously on the first `tryStart()` call and cleared by `finish()`
 * once the action settles — shared by the four action-breakdown confirm
 * dialogs (lock-reservations, acceptance-withdrawal, release-rejected-units,
 * and the availability grid's Zwolnij confirm) so all four guard the same
 * way instead of four separate hand-rolled copies.
 */
export function useSubmitGuard(): { tryStart: () => boolean; finish: () => void } {
  const isSubmittingRef = React.useRef(false);

  const tryStart = React.useCallback(() => {
    if (isSubmittingRef.current) return false;
    isSubmittingRef.current = true;
    return true;
  }, []);

  const finish = React.useCallback(() => {
    isSubmittingRef.current = false;
  }, []);

  return { tryStart, finish };
}
